/**
 * Identity Facet - Core identity attributes for agent generation
 *
 * Handles:
 * - Role seed tags selection
 * - Education and career tracks
 * - Name generation (first name, last name, full name)
 * - Languages and proficiency
 * - Gender identity and pronouns
 * - Sexual orientation and outness
 * - Naming structure, honorifics, call signs, aliases
 */

import type {
  Fixed,
  Band5,
  TierBand,
  AgentVocabV1,
  AgentPriorsV1,
  CultureProfileV1,
  Latents,
  AgentGenerationTraceV1,
  NameStructure,
  HonorificStyle,
  SexualOrientation,
  OutnessLevel,
} from '../types';

import {
  makeRng,
  facetSeed,
  clampFixed01k,
  clamp01,
  band5From01k,
  weightedPick,
  weightedPickKUnique,
  uniqueStrings,
  traceSet,
  traceFacet,
} from '../utils';

// ============================================================================
// Types
// ============================================================================

// Types for correlates that need geography context
export type DiasporaStatusInput = 'native' | 'internal-migrant' | 'expat' | 'refugee' | 'asylum-seeker' | 'dual-citizen' | 'borderland' | 'diaspora-child';
export type UrbanicityInput = 'rural' | 'small-town' | 'secondary-city' | 'capital' | 'megacity' | 'peri-urban' | 'metropolitan';

export type IdentityContext = {
  seed: string;
  vocab: AgentVocabV1;
  priors?: AgentPriorsV1;

  // Country context
  homeCountryIso3: string;
  citizenshipCountryIso3: string;
  currentCountryIso3: string;
  homeCulture: string;

  // Demographics
  age: number;
  birthYear: number;
  asOfYear: number;
  tierBand: TierBand;

  // Latents
  latents: Latents;
  cosmo01: number;
  inst01: number;
  risk01: number;
  opsec01: number;

  // Geography context for correlates (optional - set by post-hoc processing)
  diasporaStatus?: DiasporaStatusInput;
  urbanicity?: UrbanicityInput;

  // Culture context
  macroCulture: CultureProfileV1 | undefined;
  microFirstNames: string[];
  microMaleFirstNames: string[];
  microFemaleFirstNames: string[];
  microLastNames: string[];
  microLanguages: string[];
  namesPrimaryWeight: number;
  languagesPrimaryWeight: number;
  cultureTraditionalism01: number;
  microProfiles: Array<{ id: string; weight01k: number }>;

  // Environment data
  countryPriorsBucket?: {
    languages01k?: Record<string, Fixed>;
    educationTrackWeights?: Record<string, number>;
    careerTrackWeights?: Record<string, number>;
    indicators?: Record<string, unknown>;
  };
  cohortBucketStartYear: number;

  // Override inputs
  inputRoleSeedTags?: string[];

  // Trace
  trace?: AgentGenerationTraceV1;
};

export type IdentityResult = {
  // Core identity
  roleSeedTags: string[];
  educationTrackTag: string;
  careerTrackTag: string;
  effectiveBirthYear: number;

  // Name
  firstName: string;
  lastName: string;
  name: string;

  // Languages
  languages: string[];
  languageProficiencies: Array<{ language: string; proficiencyBand: Band5 }>;

  // Gender
  genderIdentityTag: string;
  genderPronounSet: string;

  // Orientation
  orientationTag: SexualOrientation;
  outnessLevel: OutnessLevel;

  // Naming system
  nameStructure: NameStructure;
  honorificStyle: HonorificStyle;
  callSign: string | null;
  aliases: string[];
  romanizedName: string | null;
};

// ============================================================================
// Main computation
// ============================================================================

export function computeIdentity(ctx: IdentityContext): IdentityResult {
  const {
    seed,
    vocab,
    priors,
    homeCountryIso3,
    citizenshipCountryIso3,
    currentCountryIso3,
    homeCulture,
    age,
    birthYear,
    asOfYear,
    tierBand,
    cosmo01,
    inst01,
    risk01,
    macroCulture,
    microFirstNames,
    microMaleFirstNames,
    microFemaleFirstNames,
    microLastNames,
    microLanguages,
    namesPrimaryWeight,
    languagesPrimaryWeight,
    cultureTraditionalism01,
    microProfiles,
    countryPriorsBucket,
    cohortBucketStartYear,
    inputRoleSeedTags,
    trace,
  } = ctx;

  // ─────────────────────────────────────────────────────────────────────────────
  // ROLE SEED TAGS
  // ─────────────────────────────────────────────────────────────────────────────
  traceFacet(trace, seed, 'base');
  const baseRng = makeRng(facetSeed(seed, 'base'));
  const roleSeedTags = (inputRoleSeedTags?.length ? inputRoleSeedTags : baseRng.pickK(vocab.identity.roleSeedTags, 2))
    .slice(0, 4);
  traceSet(trace, 'identity.roleSeedTags', roleSeedTags, {
    method: inputRoleSeedTags?.length ? 'override' : 'rng',
    dependsOn: { facet: 'base', poolSize: vocab.identity.roleSeedTags.length },
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // EDUCATION & CAREER TRACKS
  // ─────────────────────────────────────────────────────────────────────────────
  traceFacet(trace, seed, 'identity_tracks');
  const identityRng = makeRng(facetSeed(seed, 'identity_tracks'));

  const educationTracks = uniqueStrings(vocab.identity.educationTracks ?? []);
  const careerTracks = uniqueStrings(vocab.identity.careerTracks ?? []);

  // Career track nudges based on role
  const careerNudges: Record<string, string> = {
    diplomat: 'foreign-service',
    media: 'journalism',
    operative: 'intelligence',
    security: 'military',
    technocrat: 'engineering',
    analyst: 'academia',
    organizer: 'organized-labor',
    logistics: 'logistics',
  };

  const roleNudgedCareer = roleSeedTags.map(r => careerNudges[r]).find(Boolean);
  // Country indicator priors (optional; used as light nudges, never hard constraints)
  const indicators = countryPriorsBucket?.indicators;
  const gdpPerCapUsd = typeof indicators?.gdpPerCapUsd === 'number' && Number.isFinite(indicators.gdpPerCapUsd) ? indicators.gdpPerCapUsd : null;
  const exportsPctGdp = typeof indicators?.exportsPctGdp === 'number' && Number.isFinite(indicators.exportsPctGdp) ? indicators.exportsPctGdp : null;
  const importsPctGdp = typeof indicators?.importsPctGdp === 'number' && Number.isFinite(indicators.importsPctGdp) ? indicators.importsPctGdp : null;
  const airPassengersPerCap = typeof indicators?.airPassengersPerCap === 'number' && Number.isFinite(indicators.airPassengersPerCap) ? indicators.airPassengersPerCap : null;
  const militaryExpenditurePctGdp = typeof indicators?.militaryExpenditurePctGdp === 'number' && Number.isFinite(indicators.militaryExpenditurePctGdp) ? indicators.militaryExpenditurePctGdp : null;

  const gdpPerCap01 = gdpPerCapUsd != null && gdpPerCapUsd > 0 ? clamp01((Math.log10(gdpPerCapUsd) - 2.0) / 3.0, 0.5) : 0.5;
  const tradeOpen01 = (exportsPctGdp != null || importsPctGdp != null) ? clamp01(((exportsPctGdp ?? 0) + (importsPctGdp ?? 0)) / 200, 0.5) : 0.5;
  const airTravel01 = airPassengersPerCap != null ? clamp01(Math.log1p(airPassengersPerCap) / Math.log1p(6), 0.5) : 0.5;
  const militarization01 = militaryExpenditurePctGdp != null ? clamp01(Math.log1p(militaryExpenditurePctGdp) / Math.log1p(10), 0.5) : 0.5;

  const careerTrackTag = (() => {
    if (careerTracks.length === 0) return roleNudgedCareer ?? 'civil-service';
    if (roleNudgedCareer && careerTracks.includes(roleNudgedCareer)) return roleNudgedCareer;
    const weights = careerTracks.map((t) => {
      let w = 1;
      if (inst01 > 0.65 && ['civil-service', 'foreign-service', 'law', 'military', 'politics', 'public-health'].includes(t)) w += 2.2;
      if (inst01 < 0.35 && ['ngo', 'journalism', 'corporate-ops', 'academia'].includes(t)) w += 1.7;
      if (risk01 > 0.65 && ['intelligence', 'military', 'politics'].includes(t)) w += 1.3;
      if (roleNudgedCareer === t) w += 3.0;

      // Age-based career appropriateness
      // HARD CONSTRAINTS: Minimum ages for specialized careers
      // Intelligence and foreign-service require extensive training/vetting (min age 25)
      if (age < 25 && ['intelligence', 'foreign-service'].includes(t)) {
        return { item: t, weight: 0 };
      }
      // Politics at high levels requires experience (min age 28)
      if (age < 28 && t === 'politics') {
        return { item: t, weight: 0 };
      }
      // Academia requires advanced degree path (min age 28, doctorate track typical)
      // Also strongly disfavored for mass tier who rarely have graduate degrees
      if (age < 28 && t === 'academia') {
        return { item: t, weight: 0 };
      }
      if (tierBand === 'mass' && t === 'academia') {
        w *= 0.05; // Very rare for mass tier to enter academia
      }

      // Soft age preferences
      if (age < 30) {
        if (['civil-service'].includes(t)) w *= 0.6;
        if (['military', 'journalism'].includes(t)) w += 0.4;
      }
      if (age > 50) {
        if (['academia', 'civil-service', 'politics', 'foreign-service'].includes(t)) w += 0.5;
        if (['military'].includes(t)) w *= 0.7;
      }

      // Indicator nudges (trade openness, air travel, militarization, GDP)
      // These are intentionally small to avoid overfitting.
      if (tradeOpen01 > 0.65 && ['foreign-service', 'corporate-ops', 'law'].includes(t)) w += 0.6 * (tradeOpen01 - 0.65) / 0.35;
      if (airTravel01 > 0.65 && ['foreign-service', 'journalism', 'intelligence'].includes(t)) w += 0.5 * (airTravel01 - 0.65) / 0.35;
      if (militarization01 > 0.65 && ['military', 'intelligence'].includes(t)) w += 0.8 * (militarization01 - 0.65) / 0.35;
      if (gdpPerCapUsd != null) {
        if (gdpPerCap01 > 0.7 && ['academia', 'engineering', 'corporate-ops'].includes(t)) w += 0.4 * (gdpPerCap01 - 0.7) / 0.3;
        if (gdpPerCap01 < 0.35 && ['ngo', 'organized-labor', 'public-health'].includes(t)) w += 0.4 * (0.35 - gdpPerCap01) / 0.35;
      }

      const env = countryPriorsBucket?.careerTrackWeights?.[t];
      if (typeof env === 'number' && Number.isFinite(env) && env > 0) w *= env;
      return { item: t, weight: w };
    });
    const picked = weightedPick(identityRng, weights);
    if (trace) {
      trace.derived.careerTrackWeightsTop = [...weights]
        .sort((a, b) => b.weight - a.weight)
        .slice(0, 8);
    }
    return picked;
  })();

  // Deterministic Cap DC3: Opsec ↔ Career Track (Negative)
  // High opsec agents should not have high-publicness careers
  const highPublicnessCareers = ['journalism', 'corporate-ops'];
  const lowPublicnessCareers = ['intelligence', 'military', 'ngo'];
  let finalCareerTrackTag = careerTrackTag;
  if (ctx.opsec01 > 0.75 && highPublicnessCareers.includes(careerTrackTag)) {
    // Reassign to a low-publicness career that exists in the pool
    const validLowPublicness = lowPublicnessCareers.filter(c => careerTracks.includes(c));
    if (validLowPublicness.length > 0) {
      finalCareerTrackTag = identityRng.pick(validLowPublicness);
      if (trace) {
        trace.derived.dc3OpsecCareerAdjustment = {
          originalCareer: careerTrackTag,
          adjustedCareer: finalCareerTrackTag,
          opsec01: ctx.opsec01,
          reason: 'High opsec (>0.75) incompatible with high-publicness career',
        };
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DC-ID-3: Risk + Institutional → Operative Career
  // If riskAppetite > 0.7 AND institutionalEmbeddedness > 0.6, increase
  // probability of operative careers (intelligence, security, military)
  // ═══════════════════════════════════════════════════════════════════════════
  const operativeCareers = ['intelligence', 'security', 'military'];
  const nonOperativeCareers = ['civil-service', 'academia', 'journalism', 'ngo', 'corporate-ops'];
  if (risk01 > 0.7 && inst01 > 0.6 && nonOperativeCareers.includes(finalCareerTrackTag)) {
    // High risk-taking + institutional embeddedness → operative career
    const validOperative = operativeCareers.filter(c => careerTracks.includes(c));
    if (validOperative.length > 0 && identityRng.next01() < 0.65) {
      const originalCareer = finalCareerTrackTag;
      finalCareerTrackTag = identityRng.pick(validOperative);
      if (trace) {
        trace.derived.dcId3RiskInstOperativeAdjustment = {
          originalCareer,
          adjustedCareer: finalCareerTrackTag,
          risk01,
          inst01,
          reason: 'DC-ID-3: High risk (>0.7) + institutional (>0.6) → operative career',
        };
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DC-ID-5: Diaspora → Career Restriction
  // If diasporaStatus in ["refugee", "asylum-seeker"], restrict certain careers
  // (diplomacy, security clearance roles)
  // ═══════════════════════════════════════════════════════════════════════════
  const securityClearanceCareers = ['foreign-service', 'intelligence', 'military', 'security'];
  const diasporaRestrictedStatuses: DiasporaStatusInput[] = ['refugee', 'asylum-seeker'];
  if (ctx.diasporaStatus && diasporaRestrictedStatuses.includes(ctx.diasporaStatus) &&
      securityClearanceCareers.includes(finalCareerTrackTag)) {
    // Refugees/asylum-seekers cannot hold security clearance careers
    const nonClearanceCareers = careerTracks.filter(c => !securityClearanceCareers.includes(c));
    if (nonClearanceCareers.length > 0) {
      const originalCareer = finalCareerTrackTag;
      finalCareerTrackTag = identityRng.pick(nonClearanceCareers);
      if (trace) {
        trace.derived.dcId5DiasporaCareerRestriction = {
          originalCareer,
          adjustedCareer: finalCareerTrackTag,
          diasporaStatus: ctx.diasporaStatus,
          reason: 'DC-ID-5: Refugee/asylum-seeker status restricts security clearance careers',
        };
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DC-ID-7: Urbanicity → Career Match
  // Rural → agriculture, trades, local government weighted
  // Metropolitan → corporate, media, tech weighted
  // ═══════════════════════════════════════════════════════════════════════════
  const ruralCareers = ['agriculture', 'trades', 'local-government', 'public-health'];
  const metropolitanCareers = ['corporate-ops', 'journalism', 'engineering', 'academia'];
  if (ctx.urbanicity) {
    const isRural = ctx.urbanicity === 'rural' || ctx.urbanicity === 'small-town';
    const isMetro = ctx.urbanicity === 'metropolitan' || ctx.urbanicity === 'megacity' || ctx.urbanicity === 'capital';

    if (isRural && metropolitanCareers.includes(finalCareerTrackTag)) {
      // Rural agent with metro career → shift toward rural career
      const validRural = ruralCareers.filter(c => careerTracks.includes(c));
      if (validRural.length > 0 && identityRng.next01() < 0.55) {
        const originalCareer = finalCareerTrackTag;
        finalCareerTrackTag = identityRng.pick(validRural);
        if (trace) {
          trace.derived.dcId7UrbanicityCareerAdjustment = {
            originalCareer,
            adjustedCareer: finalCareerTrackTag,
            urbanicity: ctx.urbanicity,
            reason: 'DC-ID-7: Rural urbanicity shifts toward agriculture/trades/local careers',
          };
        }
      }
    } else if (isMetro && ruralCareers.includes(finalCareerTrackTag)) {
      // Metro agent with rural career → shift toward metro career
      const validMetro = metropolitanCareers.filter(c => careerTracks.includes(c));
      if (validMetro.length > 0 && identityRng.next01() < 0.55) {
        const originalCareer = finalCareerTrackTag;
        finalCareerTrackTag = identityRng.pick(validMetro);
        if (trace) {
          trace.derived.dcId7UrbanicityCareerAdjustment = {
            originalCareer,
            adjustedCareer: finalCareerTrackTag,
            urbanicity: ctx.urbanicity,
            reason: 'DC-ID-7: Metropolitan urbanicity shifts toward corporate/media/tech careers',
          };
        }
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DC-ID-8: Conditioning → Physical Career
  // If physicalConditioning > 750, increase probability of physically demanding
  // careers (military, security, athletics)
  // ═══════════════════════════════════════════════════════════════════════════
  const physicalCareers = ['military', 'security', 'athletics', 'emergency-services'];
  const sedentaryCareers = ['academia', 'civil-service', 'law', 'corporate-ops'];
  const conditioning01k = ctx.latents.physicalConditioning;
  if (conditioning01k > 750 && sedentaryCareers.includes(finalCareerTrackTag)) {
    // High conditioning + sedentary career → shift toward physical career
    const validPhysical = physicalCareers.filter(c => careerTracks.includes(c));
    if (validPhysical.length > 0 && identityRng.next01() < 0.50) {
      const originalCareer = finalCareerTrackTag;
      finalCareerTrackTag = identityRng.pick(validPhysical);
      if (trace) {
        trace.derived.dcId8ConditioningCareerAdjustment = {
          originalCareer,
          adjustedCareer: finalCareerTrackTag,
          physicalConditioning: conditioning01k,
          reason: 'DC-ID-8: High physical conditioning (>750) favors physically demanding careers',
        };
      }
    }
  }

  traceSet(trace, 'identity.careerTrackTag', finalCareerTrackTag, {
    method: 'weightedPick',
    dependsOn: { facet: 'identity_tracks', roleNudgedCareer: roleNudgedCareer ?? null, inst01, risk01, opsec01: ctx.opsec01 },
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // EDUCATION TRACK (Correlate #3: Tier ↔ Education)
  // ─────────────────────────────────────────────────────────────────────────────
  // Elite tier has much higher access to advanced education (graduate, doctorate)
  // Mass tier faces barriers to higher education, more likely trade/self-taught
  // Middle tier has moderate access, undergraduate common
	  const educationTrackTag = (() => {
	    const pool = educationTracks.length ? educationTracks : ['secondary', 'undergraduate', 'graduate', 'trade-certification'];
	    const weights = pool.map((t) => {
	      let w = 1;
      // Strong tier-education correlate: elite → advanced degrees, mass → practical training
      if (tierBand === 'elite') {
        if (['graduate', 'doctorate'].includes(t)) w += 4.5; // Elite: strong preference for advanced degrees
        if (['undergraduate'].includes(t)) w += 1.5;
        if (['secondary', 'trade-certification', 'self-taught'].includes(t)) w *= 0.3; // Rare for elite
      }
      if (tierBand === 'middle') {
        if (['undergraduate', 'graduate'].includes(t)) w += 2.0;
        if (['trade-certification'].includes(t)) w += 1.2;
      }
      if (tierBand === 'mass') {
        if (['secondary', 'trade-certification', 'self-taught'].includes(t)) w += 3.5; // Mass: practical paths
        if (['undergraduate'].includes(t)) w += 0.1;
        if (t === 'graduate') w *= 0.04; // Rare for mass tier
        if (t === 'doctorate') w *= 0.02; // Extremely rare for mass tier
        // HARD CONSTRAINT: Mass tier in low-access contexts cannot hit doctorate
        if (t === 'doctorate' && (gdpPerCap01 < 0.9 || inst01 < 0.75)) return { item: t, weight: 0 };
        // HARD CONSTRAINT: Mass tier graduate requires strong institutional access
        if (t === 'graduate' && (gdpPerCap01 < 0.7 || inst01 < 0.65)) return { item: t, weight: 0 };
      }
	      if (finalCareerTrackTag === 'military' && t === 'military-academy') w += 3.0;
	      if (finalCareerTrackTag === 'civil-service' && t === 'civil-service-track') w += 2.4;
	      // Indicator nudges: richer countries → more higher-ed, poorer → more trade/self-taught.
	      if (gdpPerCapUsd != null) {
	        if (gdpPerCap01 > 0.7 && ['undergraduate', 'graduate', 'doctorate'].includes(t)) w += 1.2 * (gdpPerCap01 - 0.7) / 0.3;
	        if (gdpPerCap01 < 0.35 && ['trade-certification', 'self-taught', 'secondary'].includes(t)) w += 1.2 * (0.35 - gdpPerCap01) / 0.35;
	      }
	      // High militarization slightly boosts military-academy likelihood where available.
	      if (militarization01 > 0.7 && t === 'military-academy') w += 0.6 * (militarization01 - 0.7) / 0.3;
	      // High trade openness slightly boosts civil-service/foreign-facing tracks.
	      if (tradeOpen01 > 0.7 && ['civil-service-track', 'undergraduate', 'graduate'].includes(t)) w += 0.4 * (tradeOpen01 - 0.7) / 0.3;
	      // HARD CONSTRAINT: Academia requires advanced education (graduate or doctorate)
	      if (finalCareerTrackTag === 'academia') {
	        if (['graduate', 'doctorate'].includes(t)) w += 6.0; // Strong preference for advanced degrees
	        if (!['graduate', 'doctorate', 'undergraduate'].includes(t)) {
	          return { item: t, weight: 0 }; // Cannot be in academia with only secondary/trade/self-taught
	        }
	        if (t === 'undergraduate') w *= 0.1; // Very rare to stay in academia with only undergrad
	      }
      if (inst01 > 0.65 && ['graduate', 'civil-service-track'].includes(t)) w += 1.2;
      const env = countryPriorsBucket?.educationTrackWeights?.[t];
      if (typeof env === 'number' && Number.isFinite(env) && env > 0) w *= env;
      return { item: t, weight: w };
    });
    let picked = weightedPick(identityRng, weights);
    if (tierBand === 'mass' && picked === 'doctorate') {
      const keepChance = gdpPerCap01 > 0.8 ? 0.15 : 0.05;
      if (identityRng.next01() > keepChance) picked = 'graduate';
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // PLAUSIBILITY GATE DC-NEW-5: Elite tier cannot have secondary/self-taught education
    // Elite status implies access to higher education opportunities
    // ═══════════════════════════════════════════════════════════════════════════
    if (tierBand === 'elite' && (picked === 'secondary' || picked === 'self-taught')) {
      picked = 'undergraduate';
      if (trace) {
        trace.derived.dcNew5EliteEducationGate = {
          originalEducation: picked,
          adjustedEducation: 'undergraduate',
          reason: 'DC-NEW-5: Elite tier cannot have secondary/self-taught education',
        };
      }
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // PLAUSIBILITY GATE DC-NEW-1: Doctorate requires age >= 30
    // Completing a doctorate typically requires 8+ years of post-secondary education
    // ═══════════════════════════════════════════════════════════════════════════
    if (age < 30 && picked === 'doctorate') {
      picked = 'graduate';
      if (trace) {
        trace.derived.dcNew1DoctorateAgeGate = {
          age,
          originalEducation: 'doctorate',
          adjustedEducation: 'graduate',
          reason: 'DC-NEW-1: Doctorate requires minimum age 30',
        };
      }
    }

    if (trace) {
      trace.derived.educationTrackWeightsTop = [...weights]
        .sort((a, b) => b.weight - a.weight)
        .slice(0, 8);
    }
    return picked;
  })();
  traceSet(trace, 'identity.educationTrackTag', educationTrackTag, {
    method: 'weightedPick',
    dependsOn: { facet: 'identity_tracks', tierBand, careerTrackTag: finalCareerTrackTag, inst01 },
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // DC-ID-6: Age + Education → Career Floor
  // If age > 40 AND education == "doctorate", career weighted toward senior/professional roles
  // Educated mid-career people should hold senior positions
  // ═══════════════════════════════════════════════════════════════════════════
  const seniorCareers = ['academia', 'law', 'civil-service', 'foreign-service', 'corporate-ops', 'politics'];
  const juniorCareers = ['journalism', 'ngo', 'organized-labor'];
  if (age > 40 && educationTrackTag === 'doctorate' && juniorCareers.includes(finalCareerTrackTag)) {
    // Mid-career doctorate holder in junior career → shift toward senior/professional role
    const validSenior = seniorCareers.filter(c => careerTracks.includes(c));
    if (validSenior.length > 0 && identityRng.next01() < 0.70) {
      const originalCareer = finalCareerTrackTag;
      finalCareerTrackTag = identityRng.pick(validSenior);
      if (trace) {
        trace.derived.dcId6AgeEducationCareerFloor = {
          originalCareer,
          adjustedCareer: finalCareerTrackTag,
          age,
          educationTrack: educationTrackTag,
          reason: 'DC-ID-6: Age >40 + doctorate → senior/professional career',
        };
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // EDUCATION-BASED AGE FLOOR
  // ─────────────────────────────────────────────────────────────────────────────
  const educationAgeFloors: Record<string, number> = {
    'secondary': 18,
    'trade-certification': 20,
    'undergraduate': 22,
    'graduate': 26,
    'doctorate': 30,
    'military-academy': 22,
    'civil-service-track': 24,
    'self-taught': 18,
  };
  const ageFloor = educationAgeFloors[educationTrackTag] ?? 18;

  let adjustedAge = age;
  let adjustedBirthYear = birthYear;
  if (age < ageFloor) {
    adjustedAge = ageFloor;
    adjustedBirthYear = asOfYear - ageFloor;
    if (trace) {
      trace.derived.ageFloorAdjustment = {
        originalAge: age,
        originalBirthYear: birthYear,
        ageFloor,
        adjustedAge,
        adjustedBirthYear,
        reason: `${educationTrackTag} requires minimum age ${ageFloor}`,
      };
    }
  }
  void adjustedAge;
  const effectiveBirthYear = adjustedBirthYear;

  // ─────────────────────────────────────────────────────────────────────────────
  // GENDER IDENTITY (moved early for gendered name selection)
  // ─────────────────────────────────────────────────────────────────────────────
  traceFacet(trace, seed, 'gender');
  const genderRng = makeRng(facetSeed(seed, 'gender'));
  const genderIdentities = vocab.gender?.identityTags ?? ['cisgender-man', 'cisgender-woman'];
  const pronounOptions = vocab.gender?.pronounSets ?? ['he-him', 'she-her', 'they-them'];

  // Two-spirit culture gating
  const indigenousAmericanCountries = new Set([
    'USA', 'CAN',
    'MEX', 'GTM', 'HND', 'SLV', 'NIC', 'CRI', 'PAN',
    'COL', 'VEN', 'ECU', 'PER', 'BOL', 'CHL', 'ARG', 'PRY',
    'BRA',
  ]);
  const isIndigenousAmericanCulture =
    homeCulture === 'Athar-West' ||
    indigenousAmericanCountries.has(homeCountryIso3) ||
    microProfiles.some(p =>
      p.id.includes('andean') ||
      p.id.includes('caribbean') ||
      p.id.includes('tawantinsuyan') ||
      p.id.includes('nahuatlan')
    );

  // Realistic gender identity distribution based on population studies:
  // ~98-99% cisgender, ~0.5-1% transgender, ~0.5-1% non-binary/other
  // (Williams Institute, Pew Research 2022)
  const genderIdentityWeights = genderIdentities.map(tag => {
    let w = 0.1; // Base weight for rare identities
    // Cisgender: ~98% combined (~49% each)
    if (tag === 'cisgender-man' || tag === 'cisgender-woman') w = 490;
    // Transgender: ~0.5% each
    if (tag === 'transgender-man' || tag === 'transgender-woman') w = 2.5;
    // Non-binary/other: ~0.5% combined
    if (tag === 'non-binary' || tag === 'genderqueer' || tag === 'agender' || tag === 'gender-fluid') w = 0.6;
    // Cultural context matters
    if (tag === 'two-spirit' && !isIndigenousAmericanCulture) w = 0;
    if (tag === 'undisclosed') w = 0.3;
    return { item: tag, weight: w };
  });
  const genderIdentityTag = weightedPick(genderRng, genderIdentityWeights);

  const pronounWeights = pronounOptions.map(pset => {
    // For cisgender identities, near-zero weight for non-matching pronouns
    // This reflects that ~99% of cisgender people use matching pronouns
    let w = 0.01; // Very low base weight
    // Cisgender: almost always use matching pronouns
    if (genderIdentityTag === 'cisgender-man') {
      w = pset === 'he-him' ? 500 : 0.1;
    }
    if (genderIdentityTag === 'cisgender-woman') {
      w = pset === 'she-her' ? 500 : 0.1;
    }
    // Transgender: strong preference for gender-aligned pronouns
    if (genderIdentityTag === 'transgender-man') {
      w = pset === 'he-him' ? 100 : (pset === 'he-they' ? 8 : (pset === 'they-them' ? 2 : 0.1));
    }
    if (genderIdentityTag === 'transgender-woman') {
      w = pset === 'she-her' ? 100 : (pset === 'she-they' ? 8 : (pset === 'they-them' ? 2 : 0.1));
    }
    // Non-binary: more varied but they-them most common
    if (genderIdentityTag === 'non-binary') {
      w = pset === 'they-them' ? 40 : ((pset === 'he-they' || pset === 'she-they') ? 20 : (pset === 'any-pronouns' ? 10 : 1));
    }
    // Genderqueer: similar to non-binary
    if (genderIdentityTag === 'genderqueer') {
      w = pset === 'they-them' ? 35 : ((pset === 'he-they' || pset === 'she-they') ? 15 : (pset === 'any-pronouns' ? 10 : 1));
    }
    // Agender: they-them predominant
    if (genderIdentityTag === 'agender') {
      w = pset === 'they-them' ? 50 : (pset === 'any-pronouns' ? 10 : 1);
    }
    // Gender-fluid: any-pronouns or mixed sets
    if (genderIdentityTag === 'gender-fluid') {
      w = pset === 'any-pronouns' ? 40 : ((pset === 'he-they' || pset === 'she-they') ? 20 : (pset === 'they-them' ? 15 : 2));
    }
    // Two-spirit: cultural variation, often they-them
    if (genderIdentityTag === 'two-spirit') {
      w = pset === 'they-them' ? 25 : (pset === 'he-him' || pset === 'she-her' ? 10 : 5);
    }
    // Undisclosed: uniform distribution
    if (genderIdentityTag === 'undisclosed') {
      w = 5;
    }
    return { item: pset, weight: w };
  });
  const genderPronounSet = weightedPick(genderRng, pronounWeights);
  traceSet(trace, 'gender', { identityTag: genderIdentityTag, pronounSet: genderPronounSet }, { method: 'weighted' });

  // Determine if we should use male or female name pool
  const useMaleNames = genderIdentityTag === 'cisgender-man' || genderIdentityTag === 'transgender-man';
  const useFemaleNames = genderIdentityTag === 'cisgender-woman' || genderIdentityTag === 'transgender-woman';

  // ─────────────────────────────────────────────────────────────────────────────
  // NAME GENERATION
  // ─────────────────────────────────────────────────────────────────────────────
  traceFacet(trace, seed, 'name');
  const nameRng = makeRng(facetSeed(seed, 'name'));
  // Use gendered name pools when available, fall back to generic firstNames
  const macroMaleFirst = macroCulture?.identity?.maleFirstNames ?? [];
  const macroFemaleFirst = macroCulture?.identity?.femaleFirstNames ?? [];
  const macroFirst = useMaleNames && macroMaleFirst.length > 0
    ? macroMaleFirst
    : useFemaleNames && macroFemaleFirst.length > 0
      ? macroFemaleFirst
      : macroCulture?.identity?.firstNames ?? [];
  const macroLast = macroCulture?.identity?.lastNames ?? [];
  const microStrength01 = microProfiles.length ? clamp01(microProfiles[0]!.weight01k / 1000, 0) : 0;
  // Select gendered micro pools
  const effectiveMicroFirstNames = useMaleNames && microMaleFirstNames.length > 0
    ? microMaleFirstNames
    : useFemaleNames && microFemaleFirstNames.length > 0
      ? microFemaleFirstNames
      : microFirstNames;

  const pickNameFromPools = (pools: Array<{ label: string; items: string[]; weight: number }>, fallbackPool: string[]) => {
    const chosen = weightedPick(nameRng, pools.map(p => ({ item: p.label, weight: p.items.length ? p.weight : 0 })));
    const pool = pools.find(p => p.label === chosen)?.items ?? [];
    const usable = pool.length ? pool : fallbackPool;
    return nameRng.pick(usable);
  };

  // Strongly prefer cultural names - only allow global pool when macro/micro pools are sparse
  const macroW = namesPrimaryWeight * (0.70 + 0.15 * cultureTraditionalism01);
  const microW = namesPrimaryWeight * 0.30 * (0.35 + 0.65 * microStrength01);
  // Only use global fallback when cultural pools are truly empty or very sparse
  const culturalCoverage = macroFirst.length > 5 && macroLast.length > 5 ? 1 : 0.5;
  const globalW = Math.max(0.01, (1 - Math.min(0.99, macroW + microW)) * (1 - culturalCoverage * 0.8));

  // Select gendered global pool when available
  const globalMaleFirst = (vocab.identity as { maleFirstNames?: string[] }).maleFirstNames ?? [];
  const globalFemaleFirst = (vocab.identity as { femaleFirstNames?: string[] }).femaleFirstNames ?? [];
  const effectiveGlobalFirst = useMaleNames && globalMaleFirst.length > 0
    ? globalMaleFirst
    : useFemaleNames && globalFemaleFirst.length > 0
      ? globalFemaleFirst
      : vocab.identity.firstNames;

  const firstName = pickNameFromPools(
    [
      { label: 'macro', items: macroFirst, weight: macroW },
      { label: 'micro', items: effectiveMicroFirstNames, weight: microW },
      { label: 'global', items: effectiveGlobalFirst, weight: globalW },
    ],
    effectiveGlobalFirst.length > 0 ? effectiveGlobalFirst : vocab.identity.firstNames,
  );
  const lastName = pickNameFromPools(
    [
      { label: 'macro', items: macroLast, weight: macroW },
      { label: 'micro', items: microLastNames, weight: microW },
      { label: 'global', items: vocab.identity.lastNames, weight: globalW },
    ],
    vocab.identity.lastNames,
  );
  const name = `${firstName} ${lastName}`;
  traceSet(trace, 'identity.name', name, {
    method: 'macro+micro+global',
    dependsOn: {
      facet: 'name',
      namesPrimaryWeight,
      genderIdentityTag,
      macroFirstPoolSize: macroFirst.length,
      microFirstPoolSize: effectiveMicroFirstNames.length,
      macroLastPoolSize: macroLast.length,
      microLastPoolSize: microLastNames.length,
      microStrength01,
    },
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // LANGUAGES & PROFICIENCY
  // ─────────────────────────────────────────────────────────────────────────────
  traceFacet(trace, seed, 'languages');
  const langRng = makeRng(facetSeed(seed, 'languages'));
  const cultureLangs = uniqueStrings([...(macroCulture?.identity?.languages ?? []), ...microLanguages]);
  const baseLangs = uniqueStrings(vocab.identity.languages);
  const unionLangs = uniqueStrings([...cultureLangs, ...baseLangs]);

  const getLanguageEnv01k = (iso3: string): Record<string, Fixed> | null => {
    const bucket = priors?.countries?.[iso3]?.buckets?.[String(cohortBucketStartYear)];
    const env = bucket?.languages01k;
    return env && typeof env === 'object' ? (env as Record<string, Fixed>) : null;
  };

  const homeLangEnv01k = getLanguageEnv01k(homeCountryIso3);
  const citizenshipLangEnv01k = citizenshipCountryIso3 !== homeCountryIso3 ? getLanguageEnv01k(citizenshipCountryIso3) : null;
  const currentLangEnv01k = currentCountryIso3 !== homeCountryIso3 ? getLanguageEnv01k(currentCountryIso3) : null;

  const maxHomeWeight = homeLangEnv01k ? Math.max(0, ...Object.values(homeLangEnv01k).map(Number)) : null;
  const homeDiversity01 = maxHomeWeight != null ? clamp01(1 - maxHomeWeight / 1000, 0) : 0;
  const abroad = currentCountryIso3 !== homeCountryIso3;

  const desiredLanguageCount = Math.max(
    1,
    Math.min(
      3,
      1 +
        (cosmo01 > 0.45 ? 1 : 0) +
        (cosmo01 > 0.78 ? 1 : 0) +
        (homeDiversity01 > 0.40 ? 1 : 0) +
        (tradeOpen01 > 0.75 ? 1 : 0) +
        (airTravel01 > 0.75 ? 1 : 0) +
        (finalCareerTrackTag === 'foreign-service' ? 1 : 0) +
        (educationTrackTag === 'graduate' || educationTrackTag === 'doctorate' ? 1 : 0),
    ),
  );
  let languageCount = Math.max(1, Math.min(3, desiredLanguageCount));

  // ═══════════════════════════════════════════════════════════════════════════
  // PLAUSIBILITY GATE DC-NEW-10: Foreign service career requires 2+ languages
  // Diplomatic work requires multilingual capability
  // ═══════════════════════════════════════════════════════════════════════════
  if (finalCareerTrackTag === 'foreign-service' && languageCount < 2) {
    languageCount = 2;
    if (trace) {
      trace.derived.dcNew10ForeignServiceLanguageGate = {
        careerTrack: 'foreign-service',
        originalLanguageCount: desiredLanguageCount,
        adjustedLanguageCount: 2,
        reason: 'DC-NEW-10: Foreign service career requires minimum 2 languages',
      };
    }
  }

  const topEnv = (env: Record<string, Fixed> | null) =>
    env
      ? Object.entries(env)
          .sort((a, b) => (Number(b[1]) - Number(a[1])) || String(a[0]).localeCompare(String(b[0])))
          .slice(0, 8)
          .map(([language, weight01k]) => ({ language, weight01k: Number(weight01k) }))
      : null;

  if (trace) {
    trace.derived.languageEnv = {
      home: topEnv(homeLangEnv01k),
      citizenship: topEnv(citizenshipLangEnv01k),
      current: topEnv(currentLangEnv01k),
      homeDiversity01,
    };
  }

  const languages: string[] = [];
  if (homeLangEnv01k) {
    const citFactor = citizenshipLangEnv01k ? 0.10 + 0.25 * cosmo01 : 0;
    const curFactor = currentLangEnv01k ? 0.15 + 0.35 * cosmo01 + (abroad ? 0.15 : 0) : 0;
    const cultureBoost = 60 * languagesPrimaryWeight;

    const primaryWeights = baseLangs.map((language) => {
      let w = 1 + (homeLangEnv01k[language] ?? 0);
      if (citizenshipLangEnv01k) w += (citizenshipLangEnv01k[language] ?? 0) * citFactor;
      if (currentLangEnv01k) w += (currentLangEnv01k[language] ?? 0) * curFactor;
      if (cultureLangs.includes(language)) w += cultureBoost;
      return { item: language, weight: w };
    });
    const primaryLanguage = weightedPick(langRng, primaryWeights);
    languages.push(primaryLanguage);

    const remainingWeights = baseLangs
      .filter(l => !languages.includes(l))
      .map((language) => {
        let w = 1 + (homeLangEnv01k[language] ?? 0);
        if (citizenshipLangEnv01k) w += (citizenshipLangEnv01k[language] ?? 0) * (0.20 + 0.30 * cosmo01);
        if (currentLangEnv01k) w += (currentLangEnv01k[language] ?? 0) * (0.25 + 0.35 * cosmo01 + (abroad ? 0.20 : 0));
        if (cultureLangs.includes(language)) w += 40;
        return { item: language, weight: w };
      });
    const remaining = Math.max(0, languageCount - languages.length);
    languages.push(...weightedPickKUnique(langRng, remainingWeights, remaining));

    traceSet(trace, 'identity.languages', languages, {
      method: 'env+weightedPickKUnique',
      dependsOn: { facet: 'languages', languageCount, homeCountryIso3, citizenshipCountryIso3, currentCountryIso3, cosmo01, homeDiversity01 },
    });
  } else {
    const useCulturePrimaryLanguage = cultureLangs.length > 0 && langRng.next01() < languagesPrimaryWeight;
    languages.push(langRng.pick(useCulturePrimaryLanguage ? cultureLangs : baseLangs));
    const remaining = Math.max(0, languageCount - languages.length);
    languages.push(...langRng.pickK(unionLangs.filter(l => !languages.includes(l)), remaining));
    traceSet(trace, 'identity.languages', languages, {
      method: 'hybridPickK',
      dependsOn: { facet: 'languages', languagesPrimaryWeight, useCulturePrimaryLanguage, languageCount, cultureLangPoolSize: cultureLangs.length, baseLangPoolSize: baseLangs.length },
    });
  }

  // Deterministic Cap DC8: High Cosmopolitanism → Minimum Languages
  // If cosmo01 > 0.75, agent MUST have at least 2 languages
  if (cosmo01 > 0.75 && languages.length < 2) {
    const availableSecondLanguages = unionLangs.filter(l => !languages.includes(l));
    if (availableSecondLanguages.length > 0) {
      // Pick a second language weighted by environment and culture
      const secondLangWeights = availableSecondLanguages.map((language) => {
        let w = 1;
        if (homeLangEnv01k) w += (homeLangEnv01k[language] ?? 0) * 0.5;
        if (citizenshipLangEnv01k) w += (citizenshipLangEnv01k[language] ?? 0) * 0.3;
        if (currentLangEnv01k) w += (currentLangEnv01k[language] ?? 0) * 0.4;
        if (cultureLangs.includes(language)) w += 30;
        return { item: language, weight: w };
      });
      const secondLanguage = weightedPick(langRng, secondLangWeights);
      languages.push(secondLanguage);
      if (trace) {
        trace.derived.dc8LanguageAdjustment = {
          cosmo01,
          originalCount: 1,
          addedLanguage: secondLanguage,
          reason: 'DC8: cosmo01 > 0.75 requires minimum 2 languages',
        };
      }
    }
  }

  // Language proficiency
  traceFacet(trace, seed, 'language_proficiency');
  const proficiencyRng = makeRng(facetSeed(seed, 'language_proficiency'));
  const languageProficiencies = languages.map((language, idx) => {
    const isPrimary = idx === 0;
    const envSupport01 = Math.max(
      (homeLangEnv01k?.[language] ?? 0) / 1000,
      (citizenshipLangEnv01k?.[language] ?? 0) / 1000,
      (currentLangEnv01k?.[language] ?? 0) / 1000,
    );
    const score = isPrimary
      ? 980
      : tierBand === 'elite'
        ? clampFixed01k(proficiencyRng.int(420, 980) + Math.round(envSupport01 * 220) + Math.round(cosmo01 * 120))
        : tierBand === 'middle'
          ? clampFixed01k(proficiencyRng.int(300, 950) + Math.round(envSupport01 * 200) + Math.round(cosmo01 * 100))
          : clampFixed01k(proficiencyRng.int(220, 900) + Math.round(envSupport01 * 180) + Math.round(cosmo01 * 80));
    const band: Band5 = band5From01k(score);
    return { language, proficiencyBand: band };
  });
  traceSet(trace, 'identity.languageProficiencies', languageProficiencies, {
    method: 'env+perLanguageBand',
    dependsOn: { facet: 'language_proficiency', tierBand, cosmo01 },
  });

  // (Gender identity was moved earlier for gendered name selection)

  // ─────────────────────────────────────────────────────────────────────────────
  // SEXUAL ORIENTATION & OUTNESS
  // ─────────────────────────────────────────────────────────────────────────────
  traceFacet(trace, seed, 'orientation');
  const orientationRng = makeRng(facetSeed(seed, 'orientation'));
  const orientationTags = vocab.orientation?.orientationTags ?? ['straight', 'gay', 'lesbian', 'bisexual', 'pansexual', 'asexual', 'queer', 'questioning', 'undisclosed'];
  const outnessLevels = vocab.orientation?.outnessLevels ?? ['closeted', 'selectively-out', 'out-to-friends', 'professionally-out', 'publicly-out'];

  // Determine gender category for orientation constraints
  const isMaleIdentified = ['cisgender-man', 'transgender-man'].includes(genderIdentityTag);
  const isFemaleIdentified = ['cisgender-woman', 'transgender-woman'].includes(genderIdentityTag);

  const orientationWeights = orientationTags.map(tag => {
    let w = 1;
    if (tag === 'straight') w = 90;
    if (tag === 'undisclosed') w = 5;
    if ((tag === 'bisexual' || tag === 'pansexual') && age < 35) w = 1.5;

    // HARD CONSTRAINT: "lesbian" is only for women, "gay" is preferred for men
    // (though "gay" can also be used by women in some contexts)
    if (tag === 'lesbian' && isMaleIdentified) {
      return { item: tag, weight: 0 }; // Men cannot be lesbian
    }
    if (tag === 'gay' && isFemaleIdentified) {
      w *= 0.3; // Women rarely use "gay" (they use "lesbian"), but it's not impossible
    }
    if (tag === 'lesbian' && isFemaleIdentified) {
      w = 1.2; // Slight preference for lesbian over gay for women
    }

    return { item: tag, weight: w };
  });
  const orientationTag = weightedPick(orientationRng, orientationWeights) as SexualOrientation;

  const outnessWeights = outnessLevels.map(level => {
    let w = 1;
    if (orientationTag === 'straight' || orientationTag === 'undisclosed') {
      if (level === 'publicly-out') w = 100;
      else w = 0;
    } else {
      if (level === 'closeted') w = 20;
      if (level === 'selectively-out') w = 25;
      if (level === 'out-to-friends') w = 30;
      if (level === 'professionally-out') w = 15;
      if (level === 'publicly-out') w = 10;
      if (tierBand === 'elite' && level === 'closeted') w *= 1.5;
      if (age < 35 && (level === 'professionally-out' || level === 'publicly-out')) w *= 1.5;
    }
    return { item: level, weight: w };
  });
  let outnessLevel = weightedPick(orientationRng, outnessWeights) as OutnessLevel;

  // ═══════════════════════════════════════════════════════════════════════════
  // PLAUSIBILITY GATE DC-NEW-8: High opsec cannot be publicly 'out'
  // Agents with high operational security discipline avoid public visibility
  // ═══════════════════════════════════════════════════════════════════════════
  if (ctx.opsec01 > 0.75 && outnessLevel === 'publicly-out') {
    outnessLevel = 'selectively-out';
    if (trace) {
      trace.derived.dcNew8OpsecOutnessGate = {
        opsec01: ctx.opsec01,
        originalOutness: 'publicly-out',
        adjustedOutness: 'selectively-out',
        reason: 'DC-NEW-8: High opsec (>0.75) incompatible with public outness',
      };
    }
  }

  traceSet(trace, 'orientation', { orientationTag, outnessLevel }, { method: 'weighted' });

  // ─────────────────────────────────────────────────────────────────────────────
  // NAMING SYSTEM
  // ─────────────────────────────────────────────────────────────────────────────
  traceFacet(trace, seed, 'naming');
  const namingRng = makeRng(facetSeed(seed, 'naming'));
  const namingStructures = vocab.naming?.structureTags ?? ['western', 'eastern-family-first', 'patronymic', 'matronymic', 'single-name', 'clan-name', 'compound-surname'];
  const honorificStyles = vocab.naming?.honorificStyles ?? ['title-first', 'given-name', 'surname', 'patronymic', 'teknonym', 'none'];
  const callSignPrefixes = vocab.naming?.callSignPrefixes ?? ['shadow', 'ghost', 'eagle', 'wolf'];

  // Check microProfiles for culture-specific naming patterns (uses shadow names)
  const profileIds = microProfiles.map(p => p.id.toLowerCase());
  const hasSolicEastCulture = profileIds.some(id =>
    id.includes('hannic') || id.includes('kamikuran') || id.includes('haedongic') || id.includes('annamitic')
  ) || homeCulture === 'Solis-East';
  const hasAramCulture = profileIds.some(id =>
    id.includes('aramaic') || id.includes('khalijic') || id.includes('levantic') || id.includes('parsic') || id.includes('kartvelian')
  ) || homeCulture === 'Aram';
  const hasIbericCulture = profileIds.some(id =>
    id.includes('castillaran') || id.includes('lusitanic') || id.includes('iberic')
  );

  const nameStructureWeights: Array<{ item: NameStructure; weight: number }> = namingStructures.map(s => {
    let w = 1;
    if (s === 'solic-family-first' && hasSolicEastCulture) w = 50;
    if (s === 'patronymic' && hasAramCulture) w = 30;
    if (s === 'hesperic' && !hasSolicEastCulture) w = 40;
    if (s === 'compound-surname' && hasIbericCulture) w = 25;
    return { item: s as NameStructure, weight: w };
  });
  const nameStructure = weightedPick(namingRng, nameStructureWeights) as NameStructure;

  const honorificWeights = honorificStyles.map(h => {
    let w = 1;
    if (h === 'surname' && tierBand === 'elite') w = 3;
    if (h === 'given-name' && age < 35) w = 2;
    if (h === 'title-first' && ['elite', 'middle'].includes(tierBand)) w = 2;
    return { item: h as HonorificStyle, weight: w };
  });
  const honorificStyle = weightedPick(namingRng, honorificWeights) as HonorificStyle;

  // Call sign for operatives/security types
  const hasCallSign = roleSeedTags.some(r => ['operative', 'security', 'technocrat'].includes(r)) && namingRng.next01() < 0.3;
  const callSign = hasCallSign
    ? `${callSignPrefixes[namingRng.int(0, callSignPrefixes.length - 1)]}-${namingRng.int(10, 99)}`
    : null;

  // Aliases
  let aliasCount = roleSeedTags.includes('operative') ? namingRng.int(1, 3) : (namingRng.next01() < 0.2 ? 1 : 0);

  // ═══════════════════════════════════════════════════════════════════════════
  // PLAUSIBILITY GATE DC-NEW-11: Intelligence career requires 1+ alias
  // Intelligence operatives need alternative identities for fieldwork
  // ═══════════════════════════════════════════════════════════════════════════
  if (finalCareerTrackTag === 'intelligence' && aliasCount < 1) {
    aliasCount = 1;
    if (trace) {
      trace.derived.dcNew11IntelligenceAliasGate = {
        careerTrack: 'intelligence',
        originalAliasCount: 0,
        adjustedAliasCount: 1,
        reason: 'DC-NEW-11: Intelligence career requires minimum 1 alias',
      };
    }
  }

  const aliases: string[] = [];
  for (let i = 0; i < aliasCount; i++) {
    const aliasFirstName = vocab.identity.firstNames[namingRng.int(0, vocab.identity.firstNames.length - 1)];
    const aliasLastName = vocab.identity.lastNames[namingRng.int(0, vocab.identity.lastNames.length - 1)];
    aliases.push(`${aliasFirstName} ${aliasLastName}`);
  }

  // Romanized name for non-Latin scripts (using shadow culture names)
  const needsRomanization = profileIds.some(id =>
    id.includes('hannic') || id.includes('kamikuran') || id.includes('haedongic') ||
    id.includes('aramaic') || id.includes('slavonic') || id.includes('helladic') ||
    id.includes('bharatic') || id.includes('siamic')
  ) || ['Solis-East', 'Solis-South', 'Aram'].includes(homeCulture);
  const romanizedName = needsRomanization ? name : null;

  traceSet(trace, 'naming', { nameStructure, honorificStyle, callSign, aliases }, { method: 'cultural' });

  return {
    roleSeedTags,
    educationTrackTag,
    careerTrackTag: finalCareerTrackTag,
    effectiveBirthYear,
    firstName,
    lastName,
    name,
    languages,
    languageProficiencies,
    genderIdentityTag,
    genderPronounSet,
    orientationTag,
    outnessLevel,
    nameStructure,
    honorificStyle,
    callSign,
    aliases,
    romanizedName,
  };
}
